import styled from 'styled-components';

export const AnalyticsContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: ${props => props.theme.spacing.sm};
  }
`;

export const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;

  h1 {
    font-size: ${props => props.theme.fontSizes['3xl']};
    font-weight: ${props => props.theme.fontWeights.bold};
    color: ${props => props.theme.colors.textPrimary};
    margin: 0 0 ${props => props.theme.spacing.sm} 0;
    font-family: ${props => props.theme.fonts.heading};
  }

  p {
    font-size: ${props => props.theme.fontSizes.base};
    color: ${props => props.theme.colors.textSecondary};
    margin: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    
    h1 {
      font-size: ${props => props.theme.fontSizes['2xl']};
    }
  }
`;

export const StudentSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  select {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    border-radius: ${props => props.theme.borderRadius.lg};
    border: 1px solid ${props => props.theme.colors.border};
    background: ${props => props.theme.colors.surface};
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.fontSizes.base};
    font-weight: ${props => props.theme.fontWeights.medium};
    cursor: pointer;
    transition: all ${props => props.theme.transitions.base};
    min-width: 200px;

    &:hover {
      border-color: ${props => props.theme.colors.primary};
    }

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 100%;
    
    select {
      width: 100%;
    }
  }
`;

export const DateRangeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  label {
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.fontSizes.sm};
  }

  input[type="date"] {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    border-radius: ${props => props.theme.borderRadius.md};
    border: 1px solid ${props => props.theme.colors.border};
    background: ${props => props.theme.colors.surface};
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.fontSizes.base};
    font-weight: ${props => props.theme.fontWeights.medium};
    cursor: pointer;
    transition: all ${props => props.theme.transitions.base};
    min-width: 160px;

    &:hover {
      border-color: ${props => props.theme.colors.primary};
    }

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 100%;
    
    input[type="date"] {
      flex: 1;
      min-width: 0;
    }
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundSecondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: ${props => props.theme.borderRadius.full};
  }
`;

export const Tab = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.base};
  white-space: nowrap;
  position: relative;
  bottom: -2px;

  ${props => props.active && `
    color: ${props.theme.colors.primary};
    border-bottom-color: ${props.theme.colors.primary};
  `}

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}10;
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    font-size: ${props => props.theme.fontSizes.sm};
  }
`;

export const Section = styled.div`
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

export const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-family: ${props => props.theme.fonts.heading};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => props.minWidth || '280px'}, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.md};
  }
`;

export const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  transition: all ${props => props.theme.transitions.base};

  &:hover {
    box-shadow: ${props => props.theme.shadows.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

export const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

export const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.color || props.theme.colors.textPrimary};
  line-height: 1.2;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes['2xl']};
  }
`;

export const StatDescription = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${props => props.theme.spacing.xs};
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  background: ${props => props.color || props.theme.colors.primary}20;
  color: ${props => props.color || props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme.colors.backgroundTertiary};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  position: relative;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.color || props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.full};
  width: ${props => props.percentage}%;
  transition: width ${props => props.theme.transitions.slow};
`;

export const CircularProgress = styled.div`
  width: ${props => props.size || '120px'};
  height: ${props => props.size || '120px'};
  border-radius: 50%;
  background: conic-gradient(
    ${props => props.color || props.theme.colors.primary} ${props => props.percentage * 3.6}deg,
    ${props => props.theme.colors.backgroundTertiary} 0deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 80%;
    height: 80%;
    background: ${props => props.theme.colors.surface};
    border-radius: 50%;
  }
`;

export const CircularProgressValue = styled.div`
  position: relative;
  z-index: 1;
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.textPrimary};
`;

export const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  margin-top: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    height: ${props => props.mobileHeight || '300px'};
  }
`;

export const AlertCard = styled(Card)`
  border-left: 4px solid ${props => {
    switch (props.severity) {
      case 'high': return props.theme.colors.error;
      case 'medium': return props.theme.colors.warning;
      case 'low': return props.theme.colors.info;
      default: return props.theme.colors.success;
    }
  }};
  background: ${props => {
    switch (props.severity) {
      case 'high': return props.theme.colors.errorLight;
      case 'medium': return props.theme.colors.warningLight;
      case 'low': return props.theme.colors.infoLight;
      default: return props.theme.colors.successLight;
    }
  }};
`;

export const AlertIcon = styled.div`
  font-size: ${props => props.theme.fontSizes['2xl']};
  margin-right: ${props => props.theme.spacing.md};
`;

export const AlertContent = styled.div`
  flex: 1;

  h4 {
    font-size: ${props => props.theme.fontSizes.base};
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
    margin: 0 0 ${props => props.theme.spacing.xs} 0;
  }

  p {
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.textSecondary};
    margin: 0;
  }
`;

export const HeatmapContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  overflow-x: auto;
  overflow-y: hidden;
  padding: ${props => props.theme.spacing.md} 0;
  
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundSecondary};
    border-radius: ${props => props.theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: ${props => props.theme.borderRadius.md};
    
    &:hover {
      background: ${props => props.theme.colors.primaryHover};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: ${props => props.theme.spacing.xs};
  }
`;

export const HeatmapDayLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-right: ${props => props.theme.spacing.xs};
  padding-top: 20px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xs};
  }
`;

export const HeatmapDayLabel = styled.div`
  height: 12px;
  display: flex;
  align-items: center;
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
  min-width: 20px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    height: 10px;
    min-width: 16px;
    font-size: 10px;
  }
`;

export const HeatmapMonths = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex: 1;
  min-width: fit-content;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: ${props => props.theme.spacing.sm};
  }
`;

export const HeatmapMonth = styled.div`
  display: flex;
  flex-direction: column;
`;

export const HeatmapMonthLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
  margin-bottom: ${props => props.theme.spacing.xs};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xs};
  }
`;

export const HeatmapWeeks = styled.div`
  display: flex;
  gap: 3px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: 2px;
  }
`;

export const HeatmapWeek = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: 2px;
  }
`;

export const HeatmapCell = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${props => props.color || props.theme.colors.backgroundTertiary};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  position: relative;
  border: 1px solid ${props => props.theme.colors.border}15;

  &:hover {
    transform: scale(1.3);
    box-shadow: ${props => props.theme.shadows.lg};
    z-index: 10;
    border-radius: 3px;

    &::after {
      content: '${props => props.tooltip}';
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background: ${props => props.theme.colors.textPrimary};
      color: ${props => props.theme.colors.textInverse};
      padding: 6px 10px;
      border-radius: ${props => props.theme.borderRadius.md};
      font-size: ${props => props.theme.fontSizes.xs};
      white-space: nowrap;
      z-index: 1000;
      box-shadow: ${props => props.theme.shadows.xl};
      pointer-events: none;
    }

    &::before {
      content: '';
      position: absolute;
      bottom: calc(100% + 2px);
      left: 50%;
      transform: translateX(-50%);
      border: 4px solid transparent;
      border-top-color: ${props => props.theme.colors.textPrimary};
      z-index: 1000;
      pointer-events: none;
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 11px;
    height: 11px;
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 10px;
    height: 10px;
    border-radius: 1px;
  }
`;

export const HeatmapLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: ${props => props.theme.spacing.xs};
  }
`;

export const HeatmapLegendLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

export const HeatmapLegendSwatch = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  background: ${props => props.color};
  border: 1px solid ${props => props.theme.colors.border}30;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 12px;
    height: 12px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['3xl']} ${props => props.theme.spacing.lg};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};

  svg {
    font-size: 64px;
    margin-bottom: ${props => props.theme.spacing.lg};
    opacity: 0.5;
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
    margin: 0 0 ${props => props.theme.spacing.sm} 0;
  }

  p {
    font-size: ${props => props.theme.fontSizes.base};
    margin: 0 0 ${props => props.theme.spacing.lg} 0;
  }
`;

export const Tooltip = styled.div`
  background: ${props => props.theme.colors.textPrimary};
  color: ${props => props.theme.colors.textInverse};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.sm};
  box-shadow: ${props => props.theme.shadows.lg};

  .label {
    font-weight: ${props => props.theme.fontWeights.semibold};
    margin-bottom: ${props => props.theme.spacing.xs};
  }

  .value {
    font-size: ${props => props.theme.fontSizes.base};
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: ${props => props.theme.spacing.xl};
  text-align: center;

  svg {
    font-size: 64px;
    color: ${props => props.theme.colors.error};
    margin-bottom: ${props => props.theme.spacing.lg};
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
    margin: 0 0 ${props => props.theme.spacing.sm} 0;
  }

  p {
    font-size: ${props => props.theme.fontSizes.base};
    color: ${props => props.theme.colors.textSecondary};
    margin: 0 0 ${props => props.theme.spacing.lg} 0;
  }
`;

export const RankingCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.xl};
`;

export const RankBadge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.color || props.theme.colors.primary};
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.fontWeights.bold};
  flex-shrink: 0;

  .percentile {
    font-size: ${props => props.theme.fontSizes['2xl']};
  }

  .suffix {
    font-size: ${props => props.theme.fontSizes.xs};
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 60px;
    height: 60px;

    .percentile {
      font-size: ${props => props.theme.fontSizes.xl};
    }
  }
`;

export const RankInfo = styled.div`
  flex: 1;

  h3 {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: ${props => props.theme.fontWeights.bold};
    color: ${props => props.theme.colors.textPrimary};
    margin: 0 0 ${props => props.theme.spacing.xs} 0;
  }

  p {
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.textSecondary};
    margin: 0;
  }
`;

